import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, ArrowRight, Bot, Building2, Check, ChevronDown, ChevronUp, Copy, CreditCard, ExternalLink, FileText, Link2, MessageCircleMore, Paperclip, PlugZap, Plus, Send, Sparkles, TimerReset, Trash2, UploadCloud, UserRound, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { getAgentBuildRequestPublicConfig, submitAgentBuildRequest } from '../../services/agent-build-request';
import { agentRequestSchema, defaultAgentRequest, stepFields } from './schema';

const DRAFT_KEY = 'verboo_agent_request_draft_v1';
const DRAFT_TTL = 7 * 24 * 60 * 60 * 1000;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_TOTAL_SIZE = 50 * 1024 * 1024;
const FILE_EXTENSIONS = {
  DOCUMENT: ['pdf', 'docx', 'xlsx', 'csv', 'txt', 'md'],
  CONVERSATION_IMAGE: ['png', 'jpg', 'jpeg', 'webp'],
};

const STEPS = [
  {
    label: 'Você',
    eyebrow: 'Quem está por trás da ideia',
    title: 'Vamos começar por você.',
    description: 'Precisamos saber com quem conversar enquanto transformamos essa ideia em um agente real.',
    icon: UserRound,
  },
  {
    label: 'Empresa',
    eyebrow: 'O contexto do negócio',
    title: 'Onde esse agente vai atuar?',
    description: 'Conte o essencial sobre a empresa, operação ou projeto que ele vai representar.',
    icon: Building2,
  },
  {
    label: 'Agente',
    eyebrow: 'Identidade e presença',
    title: 'Dê um nome e escolha os canais.',
    description: 'Você pode começar pelo WhatsApp e indicar qualquer outro lugar em que o agente precisa atender.',
    icon: Bot,
  },
  {
    label: 'Conhecimento',
    eyebrow: 'A fonte das respostas',
    title: 'O que ele precisa conhecer?',
    description: 'Envie materiais e referências. Nossa equipe cuidará de organizar e transformar isso em conhecimento útil.',
    icon: FileText,
  },
  {
    label: 'Conversas',
    eyebrow: 'Mostre em vez de explicar',
    title: 'Como seria uma conversa perfeita?',
    description: 'Escreva exemplos ou envie screenshots. Isso ajuda a capturar o tom, o raciocínio e o nível de autonomia esperado.',
    icon: MessageCircleMore,
  },
  {
    label: 'Integrações',
    eyebrow: 'Conectado ao seu negócio',
    title: 'O que o agente precisa acionar?',
    description: 'Marque sistemas e processos que devem fazer parte da solução. Não envie senhas ou chaves de acesso.',
    icon: PlugZap,
  },
  {
    label: 'Resumo',
    eyebrow: 'A visão completa',
    title: 'Em uma frase — qual é a missão?',
    description: 'Resuma o resultado que você espera. Este texto será a principal referência para nossa equipe.',
    icon: Sparkles,
  },
  {
    label: 'Modalidade',
    eyebrow: 'Escolha como entrar na fila',
    title: 'Qual modalidade faz sentido para você?',
    description: 'A escolha define em qual fila o projeto entrará. Leia os detalhes antes de enviar.',
    icon: TimerReset,
  },
];

const CHANNELS = [
  ['WHATSAPP', 'WhatsApp', 'Atendimento, vendas e suporte no canal principal.'],
  ['WEB_CHAT', 'Chat no site', 'Conversas dentro do seu site ou produto.'],
  ['INSTAGRAM', 'Instagram', 'Mensagens recebidas pelo perfil da empresa.'],
  ['VOICE', 'Voz', 'Chamadas ou experiências conversacionais por áudio.'],
  ['API', 'API', 'O agente como parte de outro sistema ou aplicativo.'],
  ['OTHER', 'Outro canal', 'Uma plataforma ou canal específico do seu negócio.'],
];

const INTEGRATIONS = [
  ['CRM', 'CRM', 'Leads, contatos, negócios e histórico comercial.'],
  ['CALENDAR', 'Agenda', 'Disponibilidade, reservas e compromissos.'],
  ['ERP', 'ERP', 'Pedidos, estoque, clientes e operação.'],
  ['ECOMMERCE', 'E-commerce', 'Catálogo, carrinho, pedidos e entregas.'],
  ['PAYMENTS', 'Pagamentos', 'Cobranças, links e confirmação de pagamento.'],
  ['HUMAN_HANDOFF', 'Atendimento humano', 'Transferência com contexto para sua equipe.'],
  ['OTHER', 'Outra integração', 'Qualquer ferramenta específica do seu fluxo.'],
];

function getDraft() {
  try {
    const draft = JSON.parse(localStorage.getItem(DRAFT_KEY));
    if (!draft || Date.now() - draft.savedAt > DRAFT_TTL) return defaultAgentRequest;
    return { ...defaultAgentRequest, ...draft.data };
  } catch {
    return defaultAgentRequest;
  }
}

function Field({ label, error, optional, children }) {
  return (
    <label className="block">
      <span className="mb-2 flex items-center gap-2 text-sm font-medium text-white">
        {label}
        {optional && <span className="text-[10px] font-normal uppercase tracking-[0.16em] text-white/35">opcional</span>}
      </span>
      {children}
      {error && <span className="mt-1.5 block text-xs font-medium text-rose-400">{error.message}</span>}
    </label>
  );
}

function SelectionCard({ selected, title, description, onClick }) {
  return (
    <button type="button" aria-pressed={selected} onClick={onClick} className={`group relative rounded-2xl border p-4 text-left transition-all duration-200 ${selected ? 'border-fuchsia-400/70 bg-fuchsia-400/10 shadow-[0_0_32px_rgba(173,52,254,0.12)]' : 'border-white/10 bg-white/[0.035] hover:border-white/20 hover:bg-white/[0.06]'}`}>
      <span className={`mb-4 flex h-5 w-5 items-center justify-center rounded-full border transition-colors ${selected ? 'border-fuchsia-400 bg-fuchsia-500 text-white' : 'border-white/25 text-transparent'}`}>
        <Check size={12} strokeWidth={3} />
      </span>
      <strong className="block text-sm font-semibold text-white">{title}</strong>
      <span className="mt-1.5 block text-xs leading-relaxed text-white/45">{description}</span>
    </button>
  );
}

function FileDropzone({ files, setFiles, onError, category, title, description, accept }) {
  const inputRef = useRef(null);
  const addFiles = (selectedFiles) => {
    try {
      const candidates = [...selectedFiles];
      const next = [...files];
      for (const file of candidates) {
        const extension = file.name.split('.').pop()?.toLowerCase();
        if (!FILE_EXTENSIONS[category].includes(extension)) throw new Error(`O formato .${extension || 'desconhecido'} não é permitido nesta etapa.`);
        if (file.size > MAX_FILE_SIZE) throw new Error(`O arquivo ${file.name} excede 10 MB.`);
        if (next.length >= 10) throw new Error('Você pode enviar no máximo 10 arquivos.');
        if (next.some((item) => item.file.name === file.name && item.file.size === file.size)) continue;
        next.push({ file, category });
      }
      if (next.reduce((total, item) => total + item.file.size, 0) > MAX_TOTAL_SIZE) {
        throw new Error('Os anexos excedem o limite total de 50 MB.');
      }
      onError('');
      setFiles(next);
    } catch (error) {
      onError(error.message);
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          addFiles(event.dataTransfer.files);
        }}
        className="flex w-full flex-col items-center rounded-2xl border border-dashed border-white/15 bg-white/[0.025] px-6 py-9 text-center transition hover:border-fuchsia-400/50 hover:bg-fuchsia-400/[0.04]"
      >
        <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-fuchsia-300">
          <UploadCloud size={22} />
        </span>
        <strong className="text-sm font-semibold text-white">{title}</strong>
        <span className="mt-1.5 max-w-md text-xs leading-relaxed text-white/40">{description}</span>
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={accept}
        className="hidden"
        onChange={(event) => {
          addFiles(event.target.files);
          event.target.value = '';
        }}
      />
    </div>
  );
}

export default function AgentRequest() {
  const [step, setStep] = useState(0);
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState('');
  const [submissionError, setSubmissionError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [priorityConfig, setPriorityConfig] = useState({
    loading: true,
    data: null,
    error: '',
  });
  const [linkCopied, setLinkCopied] = useState(false);
  const idempotencyKey = useRef(crypto.randomUUID()).current;
  const titleRef = useRef(null);
  const form = useForm({
    resolver: zodResolver(agentRequestSchema),
    defaultValues: getDraft(),
    mode: 'onBlur',
  });
  const examples = useFieldArray({
    control: form.control,
    name: 'conversationExamples',
  });
  const channels = form.watch('agent.channels');
  const integrations = form.watch('integrations');
  const serviceLevel = form.watch('serviceLevel');
  const CurrentIcon = STEPS[step].icon;

  useEffect(() => {
    let timeout;
    const subscription = form.watch((data) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ savedAt: Date.now(), data }));
      }, 500);
    });
    return () => {
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, [form]);

  const loadPriorityConfig = async () => {
    setPriorityConfig((current) => ({ ...current, loading: true, error: '' }));
    try {
      const response = await getAgentBuildRequestPublicConfig();
      const data = response?.data ?? response;
      setPriorityConfig({ loading: false, data, error: '' });
      form.setValue('priorityConfigVersion', data.version || '', {
        shouldDirty: false,
      });
      if (!data.priorityEnabled && form.getValues('serviceLevel') === 'PRIORITY') {
        form.setValue('serviceLevel', '', { shouldValidate: true });
      }
    } catch {
      setPriorityConfig({
        loading: false,
        data: null,
        error: 'Não foi possível carregar a modalidade Rápida agora.',
      });
      if (form.getValues('serviceLevel') === 'PRIORITY') form.setValue('serviceLevel', '');
    }
  };

  useEffect(() => {
    loadPriorityConfig();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    titleRef.current?.focus();
  }, [step]);

  const selectedDocumentFiles = useMemo(() => files.filter((item) => item.category === 'DOCUMENT'), [files]);
  const selectedConversationFiles = useMemo(() => files.filter((item) => item.category === 'CONVERSATION_IMAGE'), [files]);

  const addFiles = (next) => {
    setFileError('');
    setFiles(next);
  };
  const toggleChannel = (value) => {
    const next = channels.includes(value) ? channels.filter((item) => item !== value) : [...channels, value];
    form.setValue('agent.channels', next, {
      shouldValidate: true,
      shouldDirty: true,
    });
    if (value === 'OTHER' && !next.includes('OTHER')) {
      form.setValue('agent.otherChannel', '', {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };

  const toggleIntegration = (value) => {
    const exists = integrations.some((item) => item.name === value);
    const next = exists ? integrations.filter((item) => item.name !== value) : [...integrations, { name: value, purpose: '', sortOrder: integrations.length }];
    form.setValue(
      'integrations',
      next.map((item, index) => ({ ...item, sortOrder: index })),
      { shouldDirty: true }
    );
  };

  const nextStep = async () => {
    if (await form.trigger(stepFields[step], { shouldFocus: true })) setStep((current) => Math.min(current + 1, STEPS.length - 1));
  };

  const submit = form.handleSubmit(async (values) => {
    setSubmissionError('');
    setSubmitting(true);
    try {
      const metadata = {
        ...values,
        company: {
          ...values.company,
          monthlyConversationVolume: values.company.monthlyConversationVolume ?? null,
        },
        conversationExamples: values.conversationExamples.map((example, index) => ({ ...example, sortOrder: index })),
        knowledge: {
          ...values.knowledge,
          urls: values.knowledge.urls.filter(Boolean),
        },
      };
      const response = await submitAgentBuildRequest(metadata, files, idempotencyKey);
      localStorage.removeItem(DRAFT_KEY);
      setSuccess(response.data);
    } catch (error) {
      if (error?.status === 409 && values.serviceLevel === 'PRIORITY') {
        await loadPriorityConfig();
        setStep(STEPS.length - 1);
      }
      setSubmissionError(error?.data?.message || error.message || 'Não foi possível enviar sua solicitação. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  });

  if (success) {
    const priorityPending = success.serviceLevel === 'PRIORITY' && success.paymentStatus !== 'PAID';
    const queuedMessage = success.serviceLevel === 'PRIORITY'
      ? 'Seu projeto entrou na fila prioritária. Avisaremos você por WhatsApp e e-mail a cada mudança de status.'
      : 'Seu projeto entrou na fila gratuita da equipe. Avisaremos você por WhatsApp e e-mail a cada mudança de status.';
    return (
      <main className="relative min-h-screen overflow-hidden bg-[#08070b] text-white">
        <Background />
        <div className="relative z-10 flex min-h-screen items-center justify-center px-5 py-16">
          <div className="mx-auto w-full max-w-2xl rounded-[2rem] border border-white/10 bg-[#111016]/90 p-8 text-center shadow-2xl backdrop-blur-xl sm:p-14">
            <div className="mx-auto mb-7 flex h-16 w-16 items-center justify-center rounded-full border border-emerald-400/30 bg-emerald-400/10 text-emerald-300">
              <Check size={30} />
            </div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em] text-emerald-300">Briefing recebido</p>
            <h1 className="text-3xl font-semibold tracking-tight sm:text-5xl">{priorityPending ? 'Seu briefing está salvo.' : 'Sua ideia já está com a gente.'}</h1>
            <p className="mx-auto mt-5 max-w-lg text-sm leading-7 text-white/50">{priorityPending ? 'Para entrar na fila prioritária, conclua o pagamento seguro pela Stripe. Seu pedido ainda não está na fila enquanto o pagamento estiver pendente.' : queuedMessage}</p>
            <div className="mt-8 rounded-2xl border border-fuchsia-400/20 bg-fuchsia-400/[0.07] p-5 text-left">
              <p className="text-sm font-semibold text-white">Salve seu link de acompanhamento</p>
              <p className="mt-2 text-xs leading-5 text-white/45">Este link contém seu identificador secreto. Guarde-o para consultar o andamento e o histórico do atendimento.</p>
              <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                <a href={success.trackingUrl} className="min-w-0 flex-1 truncate rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-xs text-fuchsia-200 hover:border-fuchsia-400/40">
                  {success.trackingUrl}
                </a>
                <Button
                  type="button"
                  variant="outline"
                  leftIcon={Copy}
                  onClick={async () => {
                    await navigator.clipboard.writeText(success.trackingUrl);
                    setLinkCopied(true);
                  }}
                >
                  {linkCopied ? 'Copiado' : 'Copiar link'}
                </Button>
              </div>
              <a href={success.trackingUrl} className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-fuchsia-300 hover:text-fuchsia-200">
                Acompanhar agora <ExternalLink size={13} />
              </a>
            </div>
            <div className="mx-auto mt-6 inline-flex max-w-full items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs text-white/50">
              Protocolo <strong className="truncate text-white">{success.trackingId}</strong>
            </div>
            {priorityPending && (
              <Button
                type="button"
                size="lg"
                leftIcon={CreditCard}
                className="mt-7 w-full sm:w-auto"
                onClick={() => {
                  window.location.assign(success.checkoutUrl || success.trackingUrl);
                }}
              >
                {success.checkoutUrl ? 'Pagar prioridade com Stripe' : 'Abrir acompanhamento para pagar'}
              </Button>
            )}
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#08070b] text-white">
      <Background />
      <div className="relative z-10 mx-auto flex min-h-screen max-w-7xl flex-col px-4 sm:px-7 lg:px-10">
        <header className="flex items-center justify-between border-b border-white/[0.07] py-5">
          <div className="flex items-center gap-3">
            <Bot className="text-fuchsia-400" size={21} />
            <span className="text-sm font-semibold tracking-tight">
              Verboo <span className="text-white/35">Studio</span>
            </span>
          </div>
          <span className="text-[10px] font-medium uppercase tracking-[0.22em] text-white/30">Briefing de agente</span>
        </header>

        <div className="grid flex-1 gap-10 py-8 lg:grid-cols-[270px_1fr] lg:gap-16 lg:py-12">
          <aside className="hidden lg:block">
            <p className="mb-8 text-[10px] font-semibold uppercase tracking-[0.22em] text-white/25">Seu projeto</p>
            <ol className="space-y-1">
              {STEPS.map((item, index) => (
                <li key={item.label} className={`flex items-center gap-3 rounded-xl px-3 py-3 text-sm transition ${index === step ? 'bg-white/[0.065] text-white' : index < step ? 'text-white/55' : 'text-white/25'}`}>
                  <span className={`flex h-6 w-6 items-center justify-center rounded-full border text-[10px] ${index < step ? 'border-emerald-400/40 bg-emerald-400/10 text-emerald-300' : index === step ? 'border-fuchsia-400/60 bg-fuchsia-400/10 text-fuchsia-300' : 'border-white/10'}`}>{index < step ? <Check size={12} /> : index + 1}</span>
                  {item.label}
                </li>
              ))}
            </ol>
            <div className="mt-10 rounded-2xl border border-white/[0.07] bg-white/[0.025] p-4 text-xs leading-relaxed text-white/35">Seu progresso textual fica salvo neste dispositivo por 7 dias. Anexos precisam ser selecionados novamente após recarregar.</div>
          </aside>

          <section className="mx-auto w-full max-w-3xl pb-28">
            <div className="mb-8 lg:hidden">
              <div className="mb-3 flex items-center justify-between text-xs text-white/40">
                <span>
                  Etapa {step + 1} de {STEPS.length}
                </span>
                <span>{STEPS[step].label}</span>
              </div>
              <div className="h-1 overflow-hidden rounded-full bg-white/10">
                <div className="h-full rounded-full bg-gradient-to-r from-fuchsia-500 to-violet-400 transition-all" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
              </div>
            </div>

            <div className="mb-9">
              <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl border border-fuchsia-400/20 bg-fuchsia-400/[0.08] text-fuchsia-300">
                <CurrentIcon size={22} />
              </div>
              <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.25em] text-fuchsia-300/80">{STEPS[step].eyebrow}</p>
              <h1 ref={titleRef} tabIndex={-1} className="max-w-2xl text-3xl font-semibold leading-tight tracking-[-0.035em] outline-none sm:text-5xl">
                {STEPS[step].title}
              </h1>
              <p className="mt-4 max-w-2xl text-sm leading-7 text-white/45">{STEPS[step].description}</p>
            </div>

            <form onSubmit={submit}>
              <div className="rounded-[1.75rem] border border-white/[0.08] bg-[#111016]/80 p-5 shadow-[0_28px_90px_rgba(0,0,0,0.34)] backdrop-blur-xl sm:p-8">
                {step === 0 && <RequesterStep form={form} />}
                {step === 1 && <CompanyStep form={form} />}
                {step === 2 && <AgentStep form={form} channels={channels} toggleChannel={toggleChannel} />}
                {step === 3 && <KnowledgeStep form={form} files={files} setFiles={addFiles} onFileError={setFileError} selectedFiles={selectedDocumentFiles} />}
                {step === 4 && <ConversationsStep form={form} examples={examples} files={files} setFiles={addFiles} onFileError={setFileError} selectedFiles={selectedConversationFiles} />}
                {step === 5 && <IntegrationsStep form={form} integrations={integrations} toggleIntegration={toggleIntegration} />}
                {step === 6 && <SummaryStep form={form} files={files} />}
                {step === 7 && <ServiceLevelStep form={form} config={priorityConfig} />}
                {fileError && <p className="mt-5 rounded-xl border border-rose-400/20 bg-rose-400/[0.07] px-4 py-3 text-xs text-rose-300">{fileError}</p>}
                {submissionError && (
                  <p role="alert" className="mt-5 rounded-xl border border-rose-400/20 bg-rose-400/[0.07] px-4 py-3 text-sm text-rose-300">
                    {submissionError}
                  </p>
                )}
              </div>

              <footer className="mt-7 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
                <Button type="button" variant="ghost" size="lg" leftIcon={ArrowLeft} disabled={step === 0 || submitting} onClick={() => setStep((current) => current - 1)} className="w-full sm:w-auto">
                  Voltar
                </Button>
                {step < STEPS.length - 1 ? (
                  <Button type="button" variant="primary" size="lg" rightIcon={ArrowRight} onClick={nextStep} className="w-full sm:w-auto">
                    Continuar
                  </Button>
                ) : (
                  <Button type="submit" variant="primary" size="lg" rightIcon={Send} loading={submitting} disabled={!serviceLevel} className="w-full sm:w-auto">
                    Enviar briefing
                  </Button>
                )}
              </footer>
            </form>
          </section>
        </div>
      </div>
    </main>
  );
}

function RequesterStep({ form }) {
  const {
    register,
    formState: { errors },
  } = form;
  return (
    <div className="grid gap-6 sm:grid-cols-2">
      <Field label="Nome completo" error={errors.requester?.name}>
        <Input {...register('requester.name')} invalid={!!errors.requester?.name} placeholder="Como podemos chamar você?" />
      </Field>
      <Field label="E-mail" error={errors.requester?.email}>
        <Input {...register('requester.email')} invalid={!!errors.requester?.email} type="email" placeholder="voce@empresa.com" />
      </Field>
      <Field label="WhatsApp ou telefone" error={errors.requester?.phone}>
        <Input {...register('requester.phone')} invalid={!!errors.requester?.phone} placeholder="(11) 99999-9999" />
      </Field>
      <Field label="Cargo ou função" optional error={errors.requester?.role}>
        <Input {...register('requester.role')} placeholder="Ex.: Fundador, Marketing, Operações" />
      </Field>
    </div>
  );
}

function CompanyStep({ form }) {
  const {
    register,
    formState: { errors },
  } = form;
  return (
    <div className="grid gap-6 sm:grid-cols-2">
      <Field label="Nome da empresa" error={errors.company?.name}>
        <Input {...register('company.name')} invalid={!!errors.company?.name} placeholder="Nome da empresa ou projeto" />
      </Field>
      <Field label="Segmento" error={errors.company?.segment}>
        <Input {...register('company.segment')} invalid={!!errors.company?.segment} placeholder="Ex.: Saúde, varejo, educação" />
      </Field>
      <Field label="Site" optional error={errors.company?.website}>
        <Input {...register('company.website')} invalid={!!errors.company?.website} placeholder="https://suaempresa.com" />
      </Field>
      <Field label="Conversas por mês" optional error={errors.company?.monthlyConversationVolume}>
        <Input {...register('company.monthlyConversationVolume')} invalid={!!errors.company?.monthlyConversationVolume} type="number" min="0" placeholder="Ex.: 1500" />
      </Field>
    </div>
  );
}

function AgentStep({ form, channels, toggleChannel }) {
  const {
    register,
    formState: { errors },
  } = form;
  return (
    <div className="space-y-8">
      <Field label="Nome desejado para o agente" error={errors.agent?.name}>
        <Input {...register('agent.name')} invalid={!!errors.agent?.name} placeholder="Ex.: Luna, assistente da Acme" />
      </Field>
      <div>
        <p className="mb-3 text-sm font-medium text-white">Onde ele deve conversar?</p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {CHANNELS.map(([value, title, description]) => (
            <SelectionCard key={value} selected={channels.includes(value)} title={title} description={description} onClick={() => toggleChannel(value)} />
          ))}
        </div>
        {errors.agent?.channels && <span className="mt-2 block text-xs text-rose-400">{errors.agent.channels.message}</span>}
      </div>
      {channels.includes('OTHER') && (
        <Field label="Qual é o outro canal?" error={errors.agent?.otherChannel}>
          <Input {...register('agent.otherChannel')} invalid={!!errors.agent?.otherChannel} placeholder="Descreva a plataforma ou canal" />
        </Field>
      )}
    </div>
  );
}

function KnowledgeStep({ form, files, setFiles, onFileError, selectedFiles }) {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = form;
  const urls = watch('knowledge.urls');
  return (
    <div className="space-y-8">
      <Field label="Contexto importante" optional error={errors.knowledge?.context}>
        <Textarea {...register('knowledge.context')} rows={6} maxLength={10000} showCount placeholder="Produtos, serviços, regras, dúvidas frequentes, público e qualquer detalhe que ajude a entender a operação." />
      </Field>
      <div>
        <div className="mb-3 flex items-center justify-between">
          <span className="text-sm font-medium text-white">Links úteis</span>
          <button type="button" onClick={() => setValue('knowledge.urls', [...urls, ''])} className="flex items-center gap-1 text-xs text-fuchsia-300">
            <Plus size={13} /> Adicionar link
          </button>
        </div>
        <div className="space-y-3">
          {urls.map((_, index) => (
            <div className="flex gap-2" key={index}>
              <Input {...register(`knowledge.urls.${index}`)} invalid={!!errors.knowledge?.urls?.[index]} leftIcon={Link2} placeholder="https://..." />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                leftIcon={X}
                aria-label="Remover link"
                onClick={() =>
                  setValue(
                    'knowledge.urls',
                    urls.filter((__, current) => current !== index)
                  )
                }
              />
            </div>
          ))}
        </div>
      </div>
      <FileDropzone files={files} setFiles={setFiles} onError={onFileError} category="DOCUMENT" title="Adicione documentos de referência" description="PDF, DOCX, XLSX, CSV, TXT ou Markdown. Até 10 MB por arquivo." accept=".pdf,.docx,.xlsx,.csv,.txt,.md" />
      <FileList files={selectedFiles} allFiles={files} setFiles={setFiles} />
    </div>
  );
}

function ConversationsStep({ form, examples, files, setFiles, onFileError, selectedFiles }) {
  const {
    register,
    formState: { errors },
  } = form;
  return (
    <div className="space-y-8">
      <div className="space-y-4">
        {examples.fields.map((field, index) => (
          <div key={field.id} className="rounded-2xl border border-white/[0.08] bg-white/[0.025] p-4 sm:p-5">
            <div className="mb-4 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-white/35">Exemplo {index + 1}</span>
              <div className="flex gap-1">
                <Button type="button" size="icon-sm" variant="ghost" leftIcon={ChevronUp} aria-label="Mover exemplo para cima" disabled={index === 0} onClick={() => examples.swap(index, index - 1)} />
                <Button type="button" size="icon-sm" variant="ghost" leftIcon={ChevronDown} aria-label="Mover exemplo para baixo" disabled={index === examples.fields.length - 1} onClick={() => examples.swap(index, index + 1)} />
                <Button type="button" size="icon-sm" variant="ghost" leftIcon={Trash2} aria-label="Remover exemplo" onClick={() => examples.remove(index)} />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Mensagem do cliente" error={errors.conversationExamples?.[index]?.customerMessage}>
                <Textarea {...register(`conversationExamples.${index}.customerMessage`)} rows={4} placeholder="O que a pessoa diria?" />
              </Field>
              <Field label="Resposta ideal" error={errors.conversationExamples?.[index]?.idealAgentResponse}>
                <Textarea {...register(`conversationExamples.${index}.idealAgentResponse`)} rows={4} placeholder="Como o agente deveria responder?" />
              </Field>
            </div>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          leftIcon={Plus}
          className="w-full"
          onClick={() =>
            examples.append({
              customerMessage: '',
              idealAgentResponse: '',
              sortOrder: examples.fields.length,
            })
          }
        >
          Adicionar exemplo de conversa
        </Button>
      </div>
      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-white/[0.07]" />
        <span className="text-[10px] uppercase tracking-[0.2em] text-white/25">ou envie imagens</span>
        <div className="h-px flex-1 bg-white/[0.07]" />
      </div>
      <FileDropzone files={files} setFiles={setFiles} onError={onFileError} category="CONVERSATION_IMAGE" title="Envie screenshots de conversas" description="PNG, JPEG ou WebP. Remova nomes, telefones e outros dados sensíveis de terceiros." accept=".png,.jpg,.jpeg,.webp" />
      <FileList files={selectedFiles} allFiles={files} setFiles={setFiles} />
    </div>
  );
}

function IntegrationsStep({ form, integrations, toggleIntegration }) {
  const { register } = form;
  return (
    <div className="space-y-7">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {INTEGRATIONS.map(([value, title, description]) => (
          <SelectionCard key={value} selected={integrations.some((item) => item.name === value)} title={title} description={description} onClick={() => toggleIntegration(value)} />
        ))}
      </div>
      {integrations.length > 0 && (
        <div className="space-y-4 border-t border-white/[0.07] pt-7">
          {integrations.map((integration, index) => (
            <Field key={integration.name} label={`O que ${INTEGRATIONS.find(([value]) => value === integration.name)?.[1]} precisa fazer?`} optional>
              <Textarea {...register(`integrations.${index}.purpose`)} rows={3} placeholder="Descreva o resultado esperado, sem informar senhas ou chaves." />
            </Field>
          ))}
        </div>
      )}
    </div>
  );
}

function SummaryStep({ form, files }) {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = form;
  const values = watch();
  return (
    <div className="space-y-7">
      <Field label="O que este agente deve fazer e responder automaticamente?" error={errors.finalSummary}>
        <Textarea {...register('finalSummary')} invalid={!!errors.finalSummary} rows={9} maxLength={10000} showCount placeholder="Ex.: Quero que o agente entenda o que o cliente procura, recomende o produto certo, tire dúvidas, consulte disponibilidade e encaminhe para um vendedor quando houver intenção real de compra..." />
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <ReviewItem label="Agente" value={values.agent.name || 'Não informado'} />
        <ReviewItem label="Empresa" value={values.company.name || 'Não informada'} />
        <ReviewItem label="Canais" value={`${values.agent.channels.length} selecionado(s)`} />
        <ReviewItem label="Materiais" value={`${files.length} anexo(s) e ${values.knowledge.urls.filter(Boolean).length} link(s)`} />
        <ReviewItem label="Conversas" value={`${values.conversationExamples.length} exemplo(s)`} />
        <ReviewItem label="Integrações" value={`${values.integrations.length} selecionada(s)`} />
      </div>
      <div className="rounded-2xl border border-amber-300/15 bg-amber-300/[0.05] p-4 text-xs leading-relaxed text-amber-100/65">
        <strong className="mb-1 block text-amber-100">Antes de enviar</strong>
        Confirme que documentos e screenshots não contêm senhas, credenciais ou dados pessoais de terceiros sem autorização.
      </div>
      <Checkbox id="consent" checked={watch('consentAccepted')} onChange={(checked) => setValue('consentAccepted', checked, { shouldValidate: true })} label="Autorizo o processamento dos dados e anexos para análise e construção desta solução." />
      {errors.consentAccepted && <span className="block text-xs text-rose-400">{errors.consentAccepted.message}</span>}
      <input {...register('honeypot')} tabIndex={-1} autoComplete="off" className="absolute -left-[9999px] h-px w-px opacity-0" aria-hidden="true" />
    </div>
  );
}

function ServiceLevelStep({ form, config }) {
  const {
    watch,
    setValue,
    formState: { errors },
  } = form;
  const selected = watch('serviceLevel');
  const priorityAvailable = !!config.data?.priorityEnabled && !!config.data?.priorityFeeAmountCents;
  const price = priorityAvailable
    ? new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: config.data.priorityFeeCurrency || 'BRL',
      }).format(config.data.priorityFeeAmountCents / 100)
    : '';
  const choose = (value) => setValue('serviceLevel', value, { shouldValidate: true, shouldDirty: true });

  return (
    <div className="space-y-5">
      <button type="button" disabled={!priorityAvailable || config.loading} onClick={() => choose('PRIORITY')} className={`w-full rounded-2xl border p-5 text-left transition sm:p-6 ${selected === 'PRIORITY' ? 'border-fuchsia-400/70 bg-fuchsia-400/10 shadow-[0_0_36px_rgba(173,52,254,0.12)]' : 'border-white/10 bg-white/[0.03] hover:border-white/20'} disabled:cursor-not-allowed disabled:opacity-55`}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-fuchsia-300">Fila prioritária</span>
            <h2 className="mt-2 text-xl font-semibold text-white">Rápida</h2>
          </div>
          <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ${selected === 'PRIORITY' ? 'border-fuchsia-400 bg-fuchsia-500 text-white' : 'border-white/25 text-transparent'}`}>
            <Check size={13} strokeWidth={3} />
          </span>
        </div>
        <p className="mt-4 text-sm leading-6 text-white/55">Nossa equipe começa a construção o mais rápido possível e seu projeto entra na fila de prioridade após a confirmação do pagamento.</p>
        <p className="mt-3 text-xs leading-5 text-amber-100/60">Não conseguimos informar um prazo exato: ele depende do tamanho do projeto e da quantidade e complexidade das integrações.</p>
        <div className="mt-5 border-t border-white/[0.08] pt-4">
          <strong className="text-lg text-white">{config.loading ? 'Carregando valor…' : priorityAvailable ? price : 'Temporariamente indisponível'}</strong>
          {priorityAvailable && <span className="ml-2 text-xs text-white/35">taxa única de prioridade</span>}
        </div>
      </button>

      <button type="button" onClick={() => choose('FREE')} className={`w-full rounded-2xl border p-5 text-left transition sm:p-6 ${selected === 'FREE' ? 'border-emerald-400/60 bg-emerald-400/[0.08]' : 'border-white/10 bg-white/[0.03] hover:border-white/20'}`}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-300">Fila de projetos</span>
            <h2 className="mt-2 text-xl font-semibold text-white">
              Lenta <span className="text-sm font-normal text-white/40">(gratuita)</span>
            </h2>
          </div>
          <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ${selected === 'FREE' ? 'border-emerald-400 bg-emerald-500 text-white' : 'border-white/25 text-transparent'}`}>
            <Check size={13} strokeWidth={3} />
          </span>
        </div>
        <p className="mt-4 text-sm leading-6 text-white/55">Sem custo. Seu projeto entra na fila geral de projetos da equipe e será atendido conforme a disponibilidade.</p>
        <div className="mt-5 border-t border-white/[0.08] pt-4">
          <strong className="text-lg text-white">Grátis</strong>
          <span className="ml-2 text-xs text-white/35">sem cobrança</span>
        </div>
      </button>
      {config.error && <p className="text-xs text-amber-200/70">{config.error} A modalidade gratuita continua disponível.</p>}
      {errors.serviceLevel && <p className="text-xs font-medium text-rose-400">{errors.serviceLevel.message}</p>}
    </div>
  );
}

function FileList({ files, allFiles, setFiles }) {
  if (!files.length) return null;
  return (
    <div className="space-y-2">
      {files.map((item) => (
        <div key={`${item.file.name}-${item.file.size}`} className="flex items-center gap-3 rounded-xl border border-white/[0.07] bg-white/[0.03] px-4 py-3">
          <Paperclip size={15} className="text-fuchsia-300" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-medium text-white/80">{item.file.name}</p>
            <p className="mt-0.5 text-[10px] text-white/30">{(item.file.size / 1024 / 1024).toFixed(1)} MB</p>
          </div>
          <Button type="button" variant="ghost" size="icon-sm" leftIcon={X} aria-label={`Remover ${item.file.name}`} onClick={() => setFiles(allFiles.filter((candidate) => candidate !== item))} />
        </div>
      ))}
    </div>
  );
}

function ReviewItem({ label, value }) {
  return (
    <div className="rounded-xl border border-white/[0.07] bg-white/[0.025] px-4 py-3">
      <span className="block text-[10px] uppercase tracking-[0.16em] text-white/25">{label}</span>
      <strong className="mt-1 block truncate text-xs font-medium text-white/70">{value}</strong>
    </div>
  );
}

function Background() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
      <div className="absolute -left-32 top-1/4 h-96 w-96 rounded-full bg-fuchsia-700/10 blur-[120px]" />
      <div className="absolute -right-28 bottom-0 h-[30rem] w-[30rem] rounded-full bg-violet-800/10 blur-[140px]" />
      <div
        className="absolute inset-0 opacity-[0.025]"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.7) 1px, transparent 1px)',
          backgroundSize: '52px 52px',
        }}
      />
    </div>
  );
}
